import React from 'react';
import { cn } from './utils/cn';

export const CHIP_DENOMINATIONS = {
  1: {
    name: 'White',
    bg: 'bg-gradient-to-br from-gray-100 via-white to-gray-200',
    border: 'border-gray-300',
    stripe: 'bg-gray-400',
    text: 'text-gray-800',
    glow: 'drop-shadow-[0_2px_8px_rgba(255,255,255,0.3)]',
  },
  5: {
    name: 'Red',
    bg: 'bg-gradient-to-br from-red-500 via-red-600 to-red-700',
    border: 'border-red-400',
    stripe: 'bg-white',
    text: 'text-white',
    glow: 'drop-shadow-[0_2px_12px_rgba(239,68,68,0.6)]',
  },
  25: {
    name: 'Green',
    bg: 'bg-gradient-to-br from-emerald-500 via-green-600 to-emerald-700',
    border: 'border-emerald-400',
    stripe: 'bg-white',
    text: 'text-white',
    glow: 'drop-shadow-[0_2px_12px_rgba(34,197,94,0.6)]',
  },
  100: {
    name: 'Black',
    bg: 'bg-gradient-to-br from-gray-700 via-gray-900 to-black',
    border: 'border-gray-500',
    stripe: 'bg-white',
    text: 'text-white',
    glow: 'drop-shadow-[0_2px_14px_rgba(0,0,0,0.8)]',
  },
  500: {
    name: 'Purple',
    bg: 'bg-gradient-to-br from-purple-500 via-purple-600 to-purple-800',
    border: 'border-purple-400',
    stripe: 'bg-white',
    text: 'text-white',
    glow: 'drop-shadow-[0_2px_14px_rgba(168,85,247,0.7)]',
  },
  1000: {
    name: 'Orange',
    bg: 'bg-gradient-to-br from-orange-400 via-orange-500 to-orange-600',
    border: 'border-orange-300',
    stripe: 'bg-white',
    text: 'text-white',
    glow: 'drop-shadow-[0_2px_16px_rgba(249,115,22,0.7)]',
  },
  5000: {
    name: 'Gold',
    bg: 'bg-gradient-to-br from-yellow-400 via-amber-500 to-yellow-600',
    border: 'border-yellow-300',
    stripe: 'bg-white',
    text: 'text-amber-900',
    glow: 'drop-shadow-[0_2px_18px_rgba(255,215,0,0.8)]',
  },
};

export function PokerChip({
  value,
  className,
  size = 'md',
  showValue = true,
  flat = false,
  shadeLevel = 0,
  /** When true, no hover scale (stacked chips) */
  inStack = false,
  /** Layer in a vertical stack (0 = on felt). With stackCount enables depth styling. */
  stackIndex,
  stackCount,
}) {
  const chip = CHIP_DENOMINATIONS[value];
  const stackDepth =
    inStack && stackCount != null && stackIndex != null && stackCount > 0
      ? {
          isBase: stackIndex === 0,
          isTop: stackIndex === stackCount - 1,
          isSolo: stackCount === 1,
        }
      : null;

  let stackInsetShadow = '';
  let stackDropShadow = '';
  if (inStack && stackDepth) {
    if (stackDepth.isSolo) {
      stackInsetShadow =
        'shadow-[inset_0_2px_3px_rgba(255,255,255,0.22),inset_0_-3px_4px_rgba(0,0,0,0.36)]';
      stackDropShadow = 'drop-shadow-[0_5px_8px_rgba(0,32,40,0.45)]';
    } else if (stackDepth.isTop) {
      stackInsetShadow =
        'shadow-[inset_0_2px_3px_rgba(255,255,255,0.26),inset_0_-3px_4px_rgba(0,0,0,0.32)]';
      stackDropShadow = 'drop-shadow-[0_2px_5px_rgba(0,28,36,0.32)]';
    } else {
      stackInsetShadow =
        'shadow-[inset_0_2px_3px_rgba(255,255,255,0.2),inset_0_-3px_4px_rgba(0,0,0,0.35),inset_0_-11px_16px_rgba(0,18,26,0.28)]';
      stackDropShadow = stackDepth.isBase
        ? 'drop-shadow-[0_5px_8px_rgba(0,32,40,0.52)]'
        : 'drop-shadow-[0_2px_3px_rgba(0,22,30,0.35)]';
    }
  }

  const sizes = {
    xs: { chip: 'w-5 h-5', text: 'text-[5px]', flatH: 'h-[6px]', flatW: 'w-6' },
    sm: { chip: 'w-7 h-7', text: 'text-[8px]', flatH: 'h-[7px]', flatW: 'w-8' },
    md: { chip: 'w-10 h-10', text: 'text-[9px]', flatH: 'h-[8px]', flatW: 'w-11' },
    lg: { chip: 'w-14 h-14', text: 'text-xs', flatH: 'h-[10px]', flatW: 'w-16' },
  };

  const formatEdgeValue = (v) => {
    if (v >= 1000) return `${v / 1000}K`;
    return String(v);
  };

  const edgeSizes = {
    xs: 'text-[4px]',
    sm: 'text-[5px]',
    md: 'text-[6px]',
    lg: 'text-[8px]',
  };

  if (flat) {
    return (
      <div
        className={cn(
          sizes[size].flatW,
          sizes[size].flatH,
          chip.bg,
          'rounded-full border border-black/35',
          chip.border,
          chip.glow,
          'relative isolate overflow-hidden flex items-center justify-center',
          'shadow-[inset_0_1px_1px_rgba(255,255,255,0.35),inset_0_-1px_1.5px_rgba(0,0,0,0.55)]',
          'drop-shadow-[0_1px_1px_rgba(0,0,0,0.45)]',
          className
        )}
      >
        <div className={cn('absolute inset-y-0 left-1 w-[2px]', chip.stripe, 'opacity-80')} />
        <div className={cn('absolute inset-y-0 right-1 w-[2px]', chip.stripe, 'opacity-80')} />
        {shadeLevel > 0 && (
          <div className="absolute inset-0 pointer-events-none" style={{ backgroundColor: `rgba(0,0,0,${shadeLevel})` }} />
        )}
        {showValue && (
          <span
            className={cn(
              edgeSizes[size],
              'font-bold relative z-10',
              chip.text,
              'drop-shadow-[0_0.5px_1px_rgba(0,0,0,0.8)]',
              'tracking-tight'
            )}
          >
            {formatEdgeValue(value)}
          </span>
        )}
        <div className="absolute inset-0 bg-gradient-to-b from-white/30 to-transparent pointer-events-none" />
      </div>
    );
  }

  return (
    <div
      className={cn(
        sizes[size].chip,
        'relative rounded-full',
        chip.bg,
        'border-[3px]',
        chip.border,
        chip.glow,
        !inStack && 'transition-transform hover:scale-105',
        inStack && !stackDepth &&
          'shadow-[inset_0_2px_3px_rgba(255,255,255,0.25),inset_0_-3px_4px_rgba(0,0,0,0.35)] drop-shadow-[0_3px_2px_rgba(0,0,0,0.55)]',
        stackInsetShadow,
        stackDropShadow,
        inStack && stackDepth?.isTop && !stackDepth.isSolo && 'scale-[1.02] -translate-y-px ring-1 ring-white/15',
        inStack && stackDepth?.isSolo && 'ring-1 ring-white/10',
        className
      )}
    >
      <svg className="absolute inset-0 w-full h-full" viewBox="0 0 100 100" aria-hidden>
        {[0, 45, 90, 135, 180, 225, 270, 315].map((angle) => (
          <rect
            key={angle}
            x="48"
            y="2"
            width="4"
            height="10"
            rx="2"
            fill="white"
            opacity="0.9"
            transform={`rotate(${angle} 50 50)`}
          />
        ))}
      </svg>

      <div className="absolute inset-[15%] rounded-full bg-black/20 border border-white/20" />

      {showValue && (
        <div className="absolute inset-0 flex items-center justify-center">
          <span
            className={cn(
              sizes[size].text,
              'font-black leading-none tabular-nums antialiased',
              chip.text,
              'tracking-[-0.01em]',
              '[text-shadow:0_0_1px_rgba(0,0,0,0.92),0_1px_0_rgba(0,0,0,0.95)]'
            )}
          >
            {value >= 1000 ? `${value / 1000}K` : value}
          </span>
        </div>
      )}

      <div className="absolute inset-0 rounded-full bg-gradient-to-br from-white/40 via-transparent to-black/20 pointer-events-none" />
      {inStack && stackDepth && !stackDepth.isTop && (
        <div
          className="pointer-events-none absolute inset-0 rounded-full"
          style={{
            background: 'radial-gradient(ellipse 85% 55% at 50% 100%, rgba(0,12,18,0.35) 0%, transparent 62%)',
          }}
          aria-hidden
        />
      )}
    </div>
  );
}

function seededUnit(seed) {
  const x = Math.sin(seed) * 10000;
  return x - Math.floor(x);
}

/**
 * Visual stack for round chips inside `flex flex-col-reverse`.
 * DOM order: i=0 is bottom of stack, i=last is top — negative margin-bottom pulls each layer down onto the chip below (margin-top does not stack correctly with column-reverse).
 */
function getStackVisual(index, value, stackSeed = 0, size = 'xs') {
  const baseSeed = value * 97 + stackSeed * 17 + index * 31;
  const rotate = (seededUnit(baseSeed + 0.13) * 6 - 3).toFixed(2);
  const translateX = Math.round(seededUnit(baseSeed + 0.71) * 2 - 1);
  const translateY = (seededUnit(baseSeed + 0.52) * 1 - 0.5).toFixed(2);
  const shadeLevel = Number(Math.min(0.18, index * 0.035).toFixed(3));
  const chipH = size === 'sm' ? 28 : size === 'md' ? 40 : size === 'lg' ? 56 : 20;
  const overlapVariance = seededUnit(baseSeed + 0.37) > 0.55 ? 1 : 0;
  const overlap = Math.round(chipH * 0.72) - overlapVariance;
  const marginBottom = index > 0 ? -overlap : 0;

  return {
    style: {
      position: 'relative',
      zIndex: index + 1,
      marginBottom: `${marginBottom}px`,
      transform: `translate(${translateX}px, ${translateY}px) rotate(${rotate}deg)`,
      transformOrigin: 'center bottom',
      filter: shadeLevel > 0 ? `brightness(${(1 - shadeLevel).toFixed(3)})` : undefined,
    },
    shadeLevel,
  };
}

export function calculateChipBreakdown(total) {
  const breakdown = [];
  let remaining = total;

  const sortedDenominations = Object.keys(CHIP_DENOMINATIONS)
    .map(Number)
    .sort((a, b) => b - a);

  for (const denom of sortedDenominations) {
    if (remaining >= denom) {
      const count = Math.floor(remaining / denom);
      breakdown.push({ value: denom, count });
      remaining %= denom;
    }
  }

  return breakdown;
}

export function ChipStack({
  total,
  className,
  maxChipsPerColumn = 5,
  showTotal = true,
  compact = false,
  currencyLabel = 'Банк',
}) {
  const breakdown = calculateChipBreakdown(total);
  const visibleBreakdown = breakdown.slice(0, 3);
  const hiddenBreakdown = breakdown.slice(3);
  const hiddenCount = hiddenBreakdown.length;
  const hiddenValue = hiddenBreakdown.reduce((sum, chip) => sum + chip.value * chip.count, 0);
  const hasHidden = hiddenCount > 0;
  const chipSize = compact || hasHidden ? 'xs' : 'sm';

  if (breakdown.length === 0) {
    return null;
  }

  return (
    <div className={cn('flex min-w-0 max-w-full flex-col items-center gap-1.5', className)}>
      <div className="flex min-w-0 items-end justify-center gap-1">
        {visibleBreakdown.map(({ value, count }) => {
          const displayCount = Math.min(count, maxChipsPerColumn);
          const chip = CHIP_DENOMINATIONS[value];

          return (
            <div key={value} className="flex shrink-0 flex-col items-center" title={`${count}× ${value} (${chip.name})`}>
              <div className="flex w-[2.25rem] flex-col-reverse items-center justify-end sm:w-8">
                {Array.from({ length: displayCount }).map((_, i) => {
                  const visual = getStackVisual(i, value, count, chipSize);
                  return (
                    <div key={i} className="flex shrink-0 justify-center" style={visual.style}>
                      <PokerChip
                        value={value}
                        size={chipSize}
                        inStack
                        stackIndex={i}
                        stackCount={displayCount}
                        showValue={i === displayCount - 1}
                        className="transition-transform duration-200 will-change-transform"
                      />
                    </div>
                  );
                })}
              </div>

              {!compact && count > 1 && (
                <div className="mt-1 text-center">
                  <div className="text-[9px] font-bold text-foreground/70 font-mono">×{count}</div>
                </div>
              )}
            </div>
          );
        })}

        {hasHidden && (
          <div
            className="ml-0.5 flex shrink-0 flex-col items-center justify-end"
            title={`Скрыто номиналов: ${hiddenCount}. Доп. сумма: ${hiddenValue.toLocaleString()} SEKA`}
          >
            <div className="rounded-full border border-border/70 bg-black/35 px-1.5 py-[1px] text-[9px] font-bold font-mono text-foreground/80">
              +{hiddenCount}
            </div>
            {!compact && (
              <div className="mt-1 text-[8px] font-mono text-muted-foreground/90">
                +{hiddenValue.toLocaleString()}
              </div>
            )}
          </div>
        )}
      </div>

      {showTotal && (
        <div className="flex min-w-0 flex-col items-center text-center">
          <span className="truncate text-[9px] text-muted-foreground uppercase tracking-wide">{currencyLabel}</span>
          <span className="truncate text-sm font-bold text-neon-gold font-mono drop-shadow-[0_0_10px_rgba(255,215,0,0.5)] sm:text-base">
            {total.toLocaleString()} SEKA
          </span>
        </div>
      )}
    </div>
  );
}

export function CompactChipStack({ total, className }) {
  const breakdown = calculateChipBreakdown(total);
  const topChips = breakdown.slice(0, 3);

  return (
    <div className={cn('flex items-center gap-1.5', className)}>
      <div className="flex items-end gap-0.5">
        {topChips.map(({ value, count }) => {
          const displayCount = Math.min(count, 3);
          return (
            <div key={value} className="flex w-5 flex-col-reverse items-center justify-end">
              {Array.from({ length: displayCount }).map((_, i) => {
                const visual = getStackVisual(i, value, count, 'xs');
                return (
                  <div key={i} className="flex shrink-0 justify-center" style={visual.style}>
                    <PokerChip
                      value={value}
                      size="xs"
                      inStack
                      stackIndex={i}
                      stackCount={displayCount}
                      showValue={i === displayCount - 1}
                      className="transition-transform duration-200 will-change-transform"
                    />
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>
      <span className="text-[10px] font-bold text-neon-orange font-mono">{total.toLocaleString()} SEKA</span>
    </div>
  );
}
